import dayjs from 'dayjs';
import { calcLine, calcQuoteTotals } from './rfqComparison';
import { itemGstPercent } from './itemDisplay';

export function newWorkOrderId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `wo-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nextWoNumber(existing = []) {
  const year = dayjs().format('YYYY');
  const re = new RegExp(`^WO-${year}-(\\d+)$`);
  let max = 0;
  (existing || []).forEach((row) => {
    const m = String(row.wo_number || '').match(re);
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `WO-${year}-${String(max + 1).padStart(6, '0')}`;
}

export function emptyWoLine() {
  return {
    id: '',
    item_id: '',
    item_name: '',
    item_code: '',
    description: '',
    quantity: '',
    uom_id: '',
    uom_code: '',
    rate: '',
    discount_percent: '',
    gst_percent: '0',
    remarks: '',
  };
}

export function asWholeNumber(value) {
  if (value === '' || value == null) return '';
  const n = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(n)) return '';
  return String(Math.round(n));
}

export function digitsOnly(value) {
  return String(value || '').replace(/[^\d]/g, '');
}

export const WO_BREAKUP_HEADS = ['Labour', 'Parts / material', 'Visit / travel', 'Equipment', 'Other'];

export function parseWoBreakup(text) {
  const raw = String(text || '').trim();
  if (!raw) return [];
  return raw.split(/\n+/).map((line) => {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const tagged = trimmed.match(/^\s*(.+?)\s*[:\-–=]\s*₹?\s*([\d,]+(?:\.\d+)?)\s*$/);
    const spaced = trimmed.match(/^\s*(.+?)\s+₹?\s*([\d,]+(?:\.\d+)?)\s*$/);
    const hit = tagged || spaced;
    if (hit) {
      return { label: hit[1].trim(), amount: asWholeNumber(hit[2].replace(/,/g, '')) };
    }
    return { label: trimmed, amount: '' };
  }).filter(Boolean);
}

export function serializeWoBreakup(rows) {
  const defaults = new Set(WO_BREAKUP_HEADS.map((head) => head.toLowerCase()));
  return (rows || [])
    .map((row) => ({ label: String(row.label || '').trim(), amount: digitsOnly(row.amount) }))
    .filter((row) => row.label && (row.amount || !defaults.has(row.label.toLowerCase())))
    .map((row) => (row.amount ? `${row.label}: ${row.amount}` : row.label))
    .join('\n');
}

export function defaultWoBreakupRows(text) {
  const parsed = parseWoBreakup(text);
  if (parsed.length) return parsed;
  return [
    { label: 'Labour', amount: '' },
    { label: 'Parts / material', amount: '' },
    { label: 'Visit / travel', amount: '' },
  ];
}

export function woBreakupTotal(rows) {
  return (rows || []).reduce((sum, row) => sum + (Number(digitsOnly(row.amount)) || 0), 0);
}

/** Soft check: breakup heads add up to more than the quoted rate / taxable amount. */
export function woBreakupExceedsQuote(rows, quotedAmount) {
  const breakup = woBreakupTotal(rows);
  const quoted = Math.round(Number(quotedAmount) || 0);
  return breakup > 0 && quoted > 0 && breakup > quoted;
}

export function isPricedWoLine(line) {
  return Boolean(String(line?.item_name || '').trim()) && Number(line?.quantity) > 0;
}

export function woLineAmounts(line = {}) {
  return calcLine({
    qty: line.quantity,
    rate: line.rate,
    discountPercent: line.discount_percent,
    taxPercent: line.gst_percent,
  });
}

export function woFinancials(lines = []) {
  const priced = (lines || []).filter(isPricedWoLine).map((line) => ({
    ...woLineAmounts(line),
  }));
  const totals = calcQuoteTotals(priced);
  return {
    subtotal: totals.taxable,
    tax_amount: totals.tax,
    grand_total: totals.net,
    line_count: priced.length,
  };
}

export function fromApiWorkOrder(row = {}) {
  const payload = row.payload && typeof row.payload === 'object' ? row.payload : {};
  return {
    id: row.id || row.work_order_id,
    work_order_id: row.id || row.work_order_id,
    wo_number: row.wo_number || '',
    record_kind: row.record_kind || (row.mode === 'BID' && row.status !== 'ISSUED' ? 'BID' : 'WO'),
    mode: row.mode || 'DIRECT',
    status: row.status || 'DRAFT',
    title: row.title || '',
    wo_type: row.wo_type || row.work_order_type || '',
    priority: row.priority || '',
    department: row.department || row.department_name || '',
    location: row.location || '',
    asset: row.asset || '',
    description: row.description || '',
    direct_reason: row.direct_reason || '',
    rfq_id: row.rfq_id || '',
    rfq_number: row.rfq_number || '',
    quotation_number: row.quotation_number || '',
    other_reference: row.other_reference || '',
    vendor_id: row.vendor_id || '',
    vendor_name: row.vendor_name || '',
    is_open_bid: Boolean(row.is_open_bid),
    submission_deadline: row.submission_deadline || '',
    wo_date: row.wo_date || row.created_at || '',
    planned_start: row.planned_start || '',
    expected_completion: row.expected_completion || '',
    actual_start: row.actual_start || '',
    actual_completion: row.actual_completion || '',
    assigned_to: row.assigned_to || '',
    terms_and_conditions: row.terms_and_conditions || '',
    notes: row.notes || '',
    breakup: row.breakup || '',
    subtotal: Number(row.subtotal) || 0,
    tax_amount: Number(row.tax_amount) || 0,
    grand_total: Number(row.grand_total) || 0,
    source_bid_id: row.source_bid_id || '',
    issued_wo_id: row.issued_wo_id || '',
    issued_wo_number: row.issued_wo_number || payload.issued_wo_number || '',
    vendor_response: row.vendor_response || '',
    vendor_response_at: row.vendor_response_at || null,
    vendor_response_remark: row.vendor_response_remark || '',
    completed_by: row.completed_by || '',
    completion_remarks: row.completion_remarks || '',
    invited_vendor_ids: payload.invited_vendor_ids || row.invited_vendor_ids || [],
    invited_vendors: row.invited_vendors || payload.invited_vendors || [],
    attachments: row.attachments || payload.attachments || [],
    items: Array.isArray(row.items) ? row.items.map(fromApiWoItem) : [],
    quotes: Array.isArray(row.quotes) ? row.quotes.map(fromApiWoQuote) : [],
    events: Array.isArray(row.events) ? row.events : [],
    payload,
    created_by: row.created_by || '',
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function fromApiWoItem(item = {}) {
  return {
    id: item.id,
    item_id: item.item_id || '',
    item_name: item.item_name || '',
    item_code: item.item_code || '',
    description: item.description || item.specification || '',
    quantity: asWholeNumber(item.quantity ?? item.required_quantity) || '',
    uom_id: item.uom_id || '',
    uom_code: item.uom_code || '',
    rate: asWholeNumber(item.rate ?? item.unit_rate) || '',
    discount_percent: item.discount_percent ?? '',
    gst_percent: item.gst_percent ?? item.tax_percent
      ?? (itemGstPercent(item.item) || itemGstPercent(item) || '0'),
    amount: item.amount,
    remarks: item.remarks || '',
  };
}

export function fromApiWoQuote(quote = {}) {
  return {
    id: quote.id,
    work_order_id: quote.work_order_id,
    vendor_id: quote.vendor_id || '',
    vendor_name: quote.vendor_name || '',
    status: quote.status || 'SUBMITTED',
    breakup: quote.breakup || '',
    grand_total: Number(quote.grand_total) || 0,
    quoted_completion: quote.quoted_completion || '',
    remarks: quote.remarks || '',
    submitted_at: quote.submitted_at || quote.created_at,
    items: Array.isArray(quote.items) ? quote.items : [],
  };
}

export function toWoCreatePayload(form, { lines = [], vendorIds = [], attachments = [], invitedVendors = [] } = {}) {
  const finance = woFinancials(lines);
  const mode = form.mode || 'DIRECT';
  return {
    wo_number: form.wo_number,
    record_kind: mode === 'BID' ? 'BID' : 'WO',
    mode,
    status: 'DRAFT',
    title: form.title || '',
    wo_type: form.wo_type || '',
    priority: form.priority || '',
    department: form.department || '',
    location: form.location || '',
    asset: form.asset || '',
    description: form.description || '',
    direct_reason: form.direct_reason || '',
    rfq_id: form.rfq_id || undefined,
    rfq_number: form.rfq_number || '',
    quotation_number: form.quotation_number || '',
    other_reference: form.other_reference || '',
    vendor_id: mode === 'DIRECT' ? (form.vendor_id || undefined) : undefined,
    vendor_name: form.vendor_name || '',
    is_open_bid: Boolean(form.is_open_bid),
    submission_deadline: form.submission_deadline || undefined,
    wo_date: form.wo_date || dayjs().format('YYYY-MM-DD'),
    planned_start: form.planned_start || undefined,
    expected_completion: form.expected_completion || undefined,
    assigned_to: form.assigned_to || '',
    terms_and_conditions: form.terms_and_conditions || '',
    notes: form.notes || '',
    subtotal: finance.subtotal,
    tax_amount: finance.tax_amount,
    grand_total: finance.grand_total,
    attachments,
    items: (lines || []).filter(isPricedWoLine).map((line, index) => ({
      ...line,
      quantity: asWholeNumber(line.quantity) || line.quantity,
      rate: asWholeNumber(line.rate) || line.rate,
      line_no: index + 1,
      amount: woLineAmounts(line).total,
    })),
    invited_vendor_ids: mode === 'BID' ? vendorIds : [],
    invited_vendors: mode === 'BID' ? invitedVendors : [],
    payload: {
      invited_vendor_ids: mode === 'BID' ? vendorIds : [],
      invited_vendors: mode === 'BID' ? invitedVendors : [],
    },
  };
}

export function validateWoForm(form, lines, vendorIds = []) {
  if (!String(form.title || '').trim()) return 'Title is required.';
  if (!String(form.description || '').trim()) return 'Work description is required.';
  if ((lines || []).filter(isPricedWoLine).length === 0) {
    return 'Add at least one line with a name and quantity.';
  }
  if (form.mode === 'DIRECT') {
    if (!form.vendor_id) return 'Select a vendor for a Direct Work Order.';
    if (!String(form.direct_reason || '').trim()) return 'Reason for Direct Work Order is required.';
  }
  if (form.mode === 'BID') {
    if (!form.is_open_bid && !(vendorIds || []).length) {
      return 'Invite at least one vendor, or turn on Open bid.';
    }
    if (!form.submission_deadline) return 'Submission deadline is required for bidding.';
  }
  return null;
}

export function woIsBid(row) {
  const status = String(row?.status || '').toUpperCase();
  if (row?.source_bid_id) return false;
  if (['ISSUED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CLOSED'].includes(status)) return false;
  return (row?.record_kind || '') === 'BID' || row?.mode === 'BID';
}

export function woHasBidHistory(row) {
  if (!row || row.source_bid_id) return false;
  return row.mode === 'BID' || row.record_kind === 'BID';
}

/** Bid that already spawned an execution work order — hide from the default list. */
export function woIsAwardedSourceBid(row) {
  if (!woIsBid(row)) return false;
  return String(row.status || '').toUpperCase() === 'AWARDED' || Boolean(row.issued_wo_id);
}

export function woListKindLabel(row) {
  return woIsBid(row) ? 'Bid' : 'Work order';
}

export function woSourceBidNumber(row) {
  if (woIsBid(row)) return '';
  return String(row.other_reference || '').trim();
}

/**
 * One row per job: keep open bids and issued work orders.
 * Awarded source bids stay hidden unless the user filters Awarded or searches that WO #.
 */
export function visibleWorkOrderListRows(rows = [], { statusFilter = '', search = '' } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const status = String(statusFilter || '').toUpperCase();
  if (status === 'AWARDED') {
    return list.filter((row) => woIsAwardedSourceBid(row) || String(row.status || '').toUpperCase() === 'AWARDED');
  }
  const q = String(search || '').trim().toLowerCase();
  return list.filter((row) => {
    if (!woIsAwardedSourceBid(row)) return true;
    if (!q) return false;
    return String(row.wo_number || '').toLowerCase().includes(q);
  });
}

export function isHumanWoReference(value) {
  const s = String(value || '').trim();
  if (!s) return false;
  if (/^WO[-/]/i.test(s)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)) return false;
  return s.length <= 40;
}

export function formatWoActivityMeta(event = {}) {
  const actor = String(event.actor || '').trim();
  const remarks = String(event.remarks || '').replace(
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    '',
  ).replace(/[\s·#:.-]{2,}/g, ' · ').replace(/^[\s·#:.-]+|[\s·#:.-]+$/g, '').trim();
  const parts = [];
  if (actor && isHumanWoReference(actor)) parts.push(actor);
  if (event.created_at) parts.push(event.created_at);
  if (remarks) parts.push(remarks);
  return parts;
}

/** Signed EMR tokens are `wo.{payload}`; old browser-only links were `wo-{id}`. */
export function isWorkOrderPortalToken(token) {
  return /^wo[.-]/i.test(String(token || ''));
}

export function isLegacyLocalWorkOrderToken(token) {
  const value = String(token || '');
  return /^wo-/i.test(value) && !/^wo\./i.test(value);
}

/** Lowest grand total first. Rejected quotes stay out of L1/L2 ranking. */
export function rankWoQuotes(quotes = []) {
  return [...(quotes || [])]
    .filter((q) => String(q.status || '').toUpperCase() !== 'REJECTED')
    .sort((a, b) => (Number(a.grand_total) || 0) - (Number(b.grand_total) || 0))
    .map((q, i) => ({ ...q, rank: i + 1, rank_label: `L${i + 1}` }));
}

export function woQuoteRateForItem(quote, item) {
  const lines = quote?.items || [];
  const itemId = String(item?.id || item?.work_order_item_id || '');
  const name = String(item?.item_name || '');
  const hit = lines.find((line) => {
    if (line.skipped) return false;
    const lineId = String(line.work_order_item_id || line.id || '');
    return (itemId && lineId === itemId)
      || (name && String(line.item_name || '') === name);
  });
  if (!hit) return null;
  const rate = hit.rate ?? hit.unit_rate;
  return rate === '' || rate == null ? null : rate;
}

export function woIsDraft(row) {
  return (row?.status || '') === 'DRAFT';
}

export function woCanEdit(row) {
  return woIsDraft(row);
}

const BID_FLOW = ['DRAFT', 'PUBLISHED', 'ISSUED', 'IN_PROGRESS', 'COMPLETED', 'CLOSED'];
const WO_FLOW = ['DRAFT', 'ISSUED', 'IN_PROGRESS', 'COMPLETED', 'CLOSED'];

export function getWoWorkflowIndex(row) {
  const status = row?.status === 'AWARDED' ? 'ISSUED' : row?.status;
  if (status === 'CANCELLED' || status === 'ON_HOLD') return -1;
  const flow = woHasBidHistory(row) && !row?.source_bid_id ? BID_FLOW : WO_FLOW;
  const idx = flow.indexOf(status);
  return idx < 0 ? 0 : idx;
}

/** Copy the winning quote onto the same work order. Does not mint a second WO number. */
export function applyAwardedQuoteToWorkOrder(bid, quote) {
  const items = (quote.items || bid.items || []).filter((line) => !line.skipped && Number(line.rate) >= 0);
  const finance = woFinancials(items.map((line) => ({
    item_name: line.item_name,
    quantity: line.quantity,
    rate: line.rate,
    discount_percent: line.discount_percent,
    gst_percent: line.gst_percent,
  })));
  return {
    ...bid,
    record_kind: 'WO',
    mode: bid.mode || 'BID',
    status: 'ISSUED',
    vendor_id: quote.vendor_id,
    vendor_name: quote.vendor_name,
    breakup: quote.breakup || bid.breakup || '',
    expected_completion: quote.quoted_completion || bid.expected_completion,
    wo_date: bid.wo_date || dayjs().format('YYYY-MM-DD'),
    subtotal: finance.subtotal,
    tax_amount: finance.tax_amount,
    grand_total: finance.grand_total,
    source_bid_id: '',
    issued_wo_id: '',
    issued_wo_number: '',
    items: items.map((line, index) => ({
      id: line.work_order_item_id || line.id,
      item_id: line.item_id || '',
      item_name: line.item_name,
      item_code: line.item_code || '',
      description: line.description || '',
      quantity: line.quantity,
      uom_code: line.uom_code || '',
      rate: line.rate,
      discount_percent: line.discount_percent || 0,
      gst_percent: line.gst_percent || 0,
      amount: woLineAmounts(line).total,
      remarks: line.remarks || '',
      line_no: index + 1,
    })),
  };
}

export function buildAwardedWorkOrder(bid, quote) {
  return applyAwardedQuoteToWorkOrder(bid, quote);
}

export function linesFromRfq(rfqItems = [], quotes = []) {
  const quoteLines = (quotes[0]?.items || quotes[0]?.quote_items || []);
  const byItem = new Map();
  quoteLines.forEach((line) => {
    const key = line.rfq_item_id || line.item_id || line.item_name;
    if (key) byItem.set(String(key), line);
  });
  const source = rfqItems.length ? rfqItems : quoteLines;
  return source.map((item) => {
    const q = byItem.get(String(item.id)) || byItem.get(String(item.item_id)) || {};
    return {
      ...emptyWoLine(),
      item_id: item.item_id || '',
      item_name: item.item_name || q.item_name || '',
      item_code: item.item_code || '',
      description: item.specification || item.description || '',
      quantity: item.required_quantity || item.quantity || q.quoted_quantity || '',
      uom_id: item.uom_id || '',
      uom_code: item.uom_code || '',
      rate: q.unit_rate ?? q.rate ?? '',
      discount_percent: q.discount_percent ?? '',
      gst_percent: q.gst_percent ?? q.tax_percent ?? item.gst_rate ?? item.gst_percent ?? '0',
      remarks: item.remarks || '',
    };
  });
}
